import { createFileRoute, redirect, Navigate } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import type { RouterContextWithSession } from "../../../router";

const KnowledgeAtlasPage = lazy(
  () => import("../../../pages/Admin/KnowledgeAtlasPage"),
);

export const Route = createFileRoute("/_admin/knowledge-atlas/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;
    await context.queryClient.ensureQueryData({
      queryKey: ["route", "knowledge-atlas"],
      queryFn: async () => null,
      staleTime: Infinity,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Knowledge Atlas"),
  component: KnowledgeAtlasRoutePage,
});

function KnowledgeAtlasRoutePage() {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }

  return (
    <Suspense fallback={<PageSkeleton rows={4} variant="cards" />}>
      <KnowledgeAtlasPage />
    </Suspense>
  );
}
